/**
 * App Builder — apps users create, test and publish themselves.
 *
 * <p>A user app is edited as one draft and published as immutable versions.
 * Running a published one is the ordinary Apps API; everything here is the
 * authoring side.
 */

import client from './client';
import { APP_BUILDER_API, CONVERSATIONS_API } from './routes';
import type {
  AppManifest,
  AppPresentationSpec,
  AppRunDetail,
  AppRunSetup,
  AppRunsOn,
  AppVisibility,
  ViewSnapshot,
} from './apps';

export type UserAppStatus = 'DRAFT' | 'PUBLISHED' | 'DISABLED';

/** A card in "My apps": the draft as the browser may see it. */
export interface UserAppSummary {
  readonly appKey: string;
  readonly manifest: AppManifest;
  readonly status: UserAppStatus;
  readonly visibility: AppVisibility;
  readonly publishedVersion?: number | null;
  readonly hasUnpublishedChanges: boolean;
  readonly updatedAt: string;
  readonly publicLink?: boolean;
}

export interface PublicLinkState {
  /** Null when the app has no live link. */
  readonly token: string | null;
  readonly createdAt: string | null;
  /** What keeps the app from a working link; a token with problems does not resolve. */
  readonly problems: readonly string[];
}

/** Generated thumbnails are `asset:` references here; the browser-facing manifest has them as covers. */
export interface UserAppPresentation extends AppPresentationSpec {
  readonly thumbnail?: string | null;
  readonly thumbnailLight?: string | null;
}

/** The creator's own view of the draft, prompt included. */
export interface UserAppManifest extends AppManifest {
  readonly presentation?: UserAppPresentation | null;
  readonly prompt?: string | null;
  readonly allowedSkills?: readonly string[] | null;
  readonly llmModel?: string | null;
  /** Two or more allowed skills doing one job with different software; the first is the default. */
  readonly alternativeSkills?: readonly string[] | null;
}

export interface UserAppDraft {
  readonly appKey: string;
  /** Sent back with every save; a stale one is refused with 409. */
  readonly lockVersion: number;
  readonly manifest: UserAppManifest;
  /** The same draft exactly as the gallery and run screen would receive it. */
  readonly preview: AppManifest;
  readonly status: UserAppStatus;
  readonly visibility: AppVisibility;
  readonly publishedVersion?: number | null;
  readonly hasUnpublishedChanges: boolean;
  /** A thumbnail is being drawn for this app, possibly from another tab. */
  readonly thumbnailGenerating?: boolean;
}

/** The fields the creator edits by hand; anything omitted is left as it is. */
export interface DraftDetailsUpdate {
  readonly expectedLockVersion: number;
  readonly name?: string;
  readonly tagline?: string;
  readonly prompt?: string;
  readonly coverPreset?: string;
  readonly allowedSkills?: readonly string[];
  /** An empty string unpins the model, so the app runs on the default one. */
  readonly llmModel?: string;
  readonly runsOn?: AppRunsOn;
  readonly alternativeSkills?: readonly string[];
  /** Brings the cover preset back in place of a generated thumbnail. */
  readonly removeThumbnail?: boolean;
}

/** What a new app starts as; the builder agent renames it anyway. */
export interface NewUserApp {
  readonly name?: string;
  readonly tagline?: string;
  readonly allowedSkills?: readonly string[];
  readonly llmModel?: string;
}

/** Whether generated thumbnails are offered here, and what one costs in credits. */
export interface ThumbnailOffer {
  readonly available: boolean;
  readonly credits?: number | null;
}

export type AppSkillScope = 'BUILT_IN' | 'ORGANIZATION' | 'PERSONAL';

export interface AppSkillOption {
  /** The built-in name, or `custom:<uuid>` for a custom skill. */
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly scope: AppSkillScope;
  /** The runner kinds the skill can never run on: 'cloud' or 'local'. Absent from older servers. */
  readonly hiddenOn?: readonly string[];
}

/** `skipReview` is "Publish anyway": it overrides quality advice, never a security block. */
export interface PublishRequest {
  readonly expectedLockVersion: number;
  readonly skipReview?: boolean;
}

export type PublishOutcome = 'PUBLISHED' | 'REVIEW_SUGGESTED' | 'BLOCKED' | 'UNCHANGED';

/**
 * `version` is set when the app was published, or for UNCHANGED to the version
 * already live. Otherwise `findings` say why not: security problems for BLOCKED,
 * quality advice (with an optional `revisedPrompt` and the `draftLockVersion` it
 * was made on) for REVIEW_SUGGESTED.
 */
export interface PublishResult {
  readonly outcome: PublishOutcome;
  readonly version?: number | null;
  readonly findings?: readonly string[];
  readonly revisedPrompt?: string;
  readonly draftLockVersion?: number;
}

export interface AppVersionSummary {
  readonly version: number;
  readonly publishedAt: string;
  readonly live: boolean;
}

export interface CoverPreset {
  readonly id: string;
  readonly cover: string;
  readonly coverLight: string;
}

const appPath = (appKey: string) => `${APP_BUILDER_API}/apps/${encodeURIComponent(appKey)}`;

export async function listMyApps(): Promise<UserAppSummary[]> {
  const { data } = await client.get<UserAppSummary[]>(`${APP_BUILDER_API}/apps`);
  return data;
}

export async function createUserApp(details?: NewUserApp): Promise<UserAppDraft> {
  const { data } = await client.post<UserAppDraft>(`${APP_BUILDER_API}/apps`, details ?? {});
  return data;
}

export async function duplicateUserApp(appKey: string): Promise<UserAppDraft> {
  const { data } = await client.post<UserAppDraft>(`${appPath(appKey)}/duplicate`);
  return data;
}

export async function getUserAppDraft(appKey: string): Promise<UserAppDraft> {
  const { data } = await client.get<UserAppDraft>(`${appPath(appKey)}/draft`);
  return data;
}

export async function updateUserAppDraft(
  appKey: string,
  update: DraftDetailsUpdate,
): Promise<UserAppDraft> {
  const { data } = await client.patch<UserAppDraft>(`${appPath(appKey)}/draft`, update);
  return data;
}

export async function publishUserApp(
  appKey: string,
  request: PublishRequest,
): Promise<PublishResult> {
  const { data } = await client.post<PublishResult>(`${appPath(appKey)}/publish`, request);
  return data;
}

export async function restoreUserAppVersion(
  appKey: string,
  version: number,
  expectedLockVersion: number,
): Promise<UserAppDraft> {
  const { data } = await client.post<UserAppDraft>(
    `${appPath(appKey)}/versions/${version}/restore`,
    { expectedLockVersion },
  );
  return data;
}

/** A run of the caller's own draft, opened the way any run is and shown on the run page. */
export async function startTestRun(appKey: string): Promise<AppRunDetail> {
  const { data } = await client.post<AppRunDetail>(`${appPath(appKey)}/test-runs`);
  return data;
}

/** Copy a finished test run's files, settings and first instruction into the app as its example. */
export async function promoteRunToExample(appKey: string, runId: string): Promise<UserAppDraft> {
  const { data } = await client.post<UserAppDraft>(`${appPath(appKey)}/example`, { runId });
  return data;
}

/** An app that never runs the agent keeps the settings it opens with as its example. */
export async function keepOptionsAsExample(
  appKey: string,
  options: Readonly<Record<string, unknown>>,
): Promise<UserAppDraft> {
  const { data } = await client.post<UserAppDraft>(`${appPath(appKey)}/example/options`, { options });
  return data;
}

/** Where the owner's draft may run and how ready their runners are, before it is published. */
export async function getDraftRunSetup(appKey: string): Promise<AppRunSetup> {
  const { data } = await client.get<AppRunSetup>(`${appPath(appKey)}/run-setup`);
  return data;
}

export async function getThumbnailOffer(): Promise<ThumbnailOffer> {
  const { data } = await client.get<ThumbnailOffer>(`${APP_BUILDER_API}/thumbnail-offer`);
  return data;
}

/** Draws the app's dark and light thumbnails; takes up to a couple of minutes and is charged. */
export async function generateThumbnail(appKey: string, direction?: string): Promise<UserAppDraft> {
  const { data } = await client.post<UserAppDraft>(
    `${appPath(appKey)}/thumbnail`,
    { direction: direction?.trim() || undefined },
  );
  return data;
}

export const SKETCH_VIEWERS = ['model', 'room', 'simulation', 'image', 'chart', 'document', 'drawing', 'generic'] as const;
export type SketchViewer = (typeof SKETCH_VIEWERS)[number];
export type SketchControlKind = 'slider' | 'select' | 'chips' | 'toggle' | 'text';

export interface AppSketch {
  readonly viewers: readonly SketchViewer[];
  readonly controls: readonly { readonly label: string; readonly kind: SketchControlKind }[];
}

export async function sketchApp(appKey: string, brief: string): Promise<AppSketch> {
  const { data } = await client.post<AppSketch>(`${appPath(appKey)}/sketch`, { brief });
  return data;
}

/** A PNG; anything else is turned into one first. */
export async function uploadAppLogo(appKey: string, png: Blob): Promise<UserAppDraft> {
  const formData = new FormData();
  formData.append('file', png, 'logo.png');
  const { data } = await client.put<UserAppDraft>(`${appPath(appKey)}/logo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function removeAppLogo(appKey: string): Promise<UserAppDraft> {
  const { data } = await client.delete<UserAppDraft>(`${appPath(appKey)}/logo`);
  return data;
}

export type TestRunDiagnosticKind = 'PAGE_ERROR' | 'KIT_ERROR' | 'REQUEST_FAILED';

export interface TestRunDiagnostic {
  readonly kind: TestRunDiagnosticKind;
  readonly method?: string;
  readonly code?: string;
  readonly message: string;
  readonly occurrences: number;
}

/** Best effort: tells the builder assistant what went wrong on the page. */
export async function reportTestRunDiagnostics(
  appKey: string,
  runId: string,
  entries: readonly TestRunDiagnostic[],
): Promise<void> {
  await client.post(
    `${appPath(appKey)}/test-runs/${runId}/diagnostics`,
    { entries },
    { skipAuthRedirect: true },
  );
}

export interface PreviewCaptureUpload {
  readonly snapshot?: ViewSnapshot;
  readonly pageErrors?: readonly string[];
  readonly failure?: string;
}

export async function answerPreviewCapture(appKey: string, captureId: string, upload: PreviewCaptureUpload): Promise<void> {
  await client.post(`${appPath(appKey)}/preview-captures/${encodeURIComponent(captureId)}`, upload, {
    skipAuthRedirect: true,
  });
}

export async function listUserAppVersions(appKey: string): Promise<AppVersionSummary[]> {
  const { data } = await client.get<AppVersionSummary[]>(`${appPath(appKey)}/versions`);
  return data;
}

export async function setUserAppVisibility(
  appKey: string,
  visibility: AppVisibility,
): Promise<void> {
  await client.put(`${appPath(appKey)}/visibility`, { visibility });
}

/** An organization admin taking a colleague's app back to private. */
export async function unshareUserApp(appKey: string): Promise<void> {
  await client.post(`${appPath(appKey)}/unshare`);
}

export async function getPublicLink(appKey: string): Promise<PublicLinkState> {
  const { data } = await client.get<PublicLinkState>(`${appPath(appKey)}/public-link`);
  return data;
}

/** Turns the link on, or replaces it: the previous URL stops working for everyone. */
export async function createPublicLink(appKey: string): Promise<PublicLinkState> {
  const { data } = await client.post<PublicLinkState>(`${appPath(appKey)}/public-link`);
  return data;
}

export async function revokePublicLink(appKey: string): Promise<void> {
  await client.delete(`${appPath(appKey)}/public-link`);
}

export async function deleteUserApp(appKey: string): Promise<void> {
  await client.delete(appPath(appKey));
}

export async function listCoverPresets(): Promise<CoverPreset[]> {
  const { data } = await client.get<CoverPreset[]>(`${APP_BUILDER_API}/cover-presets`);
  return data;
}

export async function listAppSkills(): Promise<AppSkillOption[]> {
  const { data } = await client.get<AppSkillOption[]>(`${APP_BUILDER_API}/skills`);
  return data;
}

/** A conversation bound to one of the caller's apps: the builder chat. */
export interface AppBuilderConversation {
  readonly conversationId: string;
}

// A 403/404 here means the builder is not available for this app, not that
// the session is dead, so it must not log the user out.
const BUILDER_CONVERSATION_REQUEST = { skipAuthRedirect: true } as const;

export async function listAppBuilderConversations(
  appKey: string,
): Promise<readonly AppBuilderConversation[]> {
  const { data } = await client.get<readonly AppBuilderConversation[]>(CONVERSATIONS_API, {
    params: { appBuilderAppKey: appKey },
    ...BUILDER_CONVERSATION_REQUEST,
  });
  return data;
}

export async function createAppBuilderConversation(
  appKey: string,
): Promise<AppBuilderConversation> {
  const { data } = await client.post<AppBuilderConversation>(
    CONVERSATIONS_API,
    { appBuilderAppKey: appKey },
    BUILDER_CONVERSATION_REQUEST,
  );
  return data;
}
